/**
 * Runs the provided function for each input while ensuring no more than parallelCount occur simultaneously
 * @param parallelCount How many jobs can occur simultaneously
 * @param inputs The array of input data to be passed piecemeal to the job factory
 * @param jobFactory The async function that will be invoked once per input
 * @returns
 */
export const asyncRequestQueue = async <InputType, OutputType>(
  parallelCount: number,
  inputs: Array<InputType>,
  jobFactory: (input: InputType) => Promise<OutputType>
): Promise<OutputType[]> => {
  if (inputs.length === 0) return Promise.resolve([]);

  return new Promise<OutputType[]>((resolveAll, rejectAll) => {
    const pendingInputs = [...inputs];
    const outputs = new Array<OutputType>();
    let finishedCount = 0;

    const startNextJob = async () => {
      if (finishedCount === inputs.length) {
        resolveAll(outputs);
        return;
      }
      if (pendingInputs.length === 0) {
        return;
      }
      const index = inputs.length - pendingInputs.length;
      const input = pendingInputs.shift();
      try {
        outputs[index] = await jobFactory(input);
        finishedCount++;
      } catch (error) {
        rejectAll(error);
        pendingInputs.length = 0;
      }
      void startNextJob();
    };

    const jobsToStart = Math.min(parallelCount, inputs.length);
    for (let i = 0; i < jobsToStart; i++) {
      void startNextJob();
    }
  });
};
